import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft, RotateCcw, Trash2, Loader2, TriangleAlert,
} from "lucide-react";
import { api } from "../lib/api";
import { useWorkspace } from "../lib/WorkspaceContext";
import { useLocaleFormat } from "../i18n/useLocaleFormat";
import DocTypeIcon from "../components/documents/DocTypeIcon";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../components/ui/alert-dialog";

/**
 * Trash page (iter62 redesign)
 *
 * UX:
 *   • Liste des documents soft-deleted avec icône de type, nom, date.
 *   • Bouton "Restaurer" — accessible à tous les rôles autorisés (admin+).
 *   • Bouton "Supprimer définitivement" par doc — owner only (purge DB+storage+alerts).
 *   • Bouton "Vider la corbeille" en haut à droite — owner only.
 *   • Empty state avec icône Trash2.
 *   • i18n FR/EN complet, plus aucun texte anglais hardcodé.
 *
 * Backend wiring (iter62):
 *   - GET    /api/documents/trash
 *   - POST   /api/documents/{id}/restore   (admin+)
 *   - DELETE /api/documents/{id}/purge     (owner only, 204)
 *   - DELETE /api/documents/trash/purge-all (owner only, {purged_count})
 */
export default function Trash() {
  const { t } = useTranslation("trash");
  const navigate = useNavigate();
  const { formatDate } = useLocaleFormat();
  const { current } = useWorkspace();
  const isOwner = (current?.role || "") === "owner";

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [purgingId, setPurgingId] = useState(null);
  const [purgingAll, setPurgingAll] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(null);   // doc object
  const [confirmPurgeAll, setConfirmPurgeAll] = useState(false);

  const refresh = async () => {
    try {
      const res = await api.get("/documents/trash");
      setDocs(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const restore = async (id) => {
    setRestoringId(id);
    try {
      await api.post(`/documents/${id}/restore`);
      toast.success(t("toasts.restored"));
      await refresh();
    } catch (e) {
      toast.error(`${t("toasts.restored_failed")} — ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setRestoringId(null);
    }
  };

  const purge = async (doc) => {
    setPurgingId(doc.id);
    try {
      await api.delete(`/documents/${doc.id}/purge`);
      toast.success(t("toasts.purged"));
      setConfirmPurge(null);
      await refresh();
    } catch (e) {
      toast.error(`${t("toasts.purged_failed")} — ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setPurgingId(null);
    }
  };

  const purgeAll = async () => {
    setPurgingAll(true);
    try {
      const res = await api.delete("/documents/trash/purge-all");
      toast.success(t("toasts.purge_all_done", { count: res.data?.purged_count ?? 0 }));
      setConfirmPurgeAll(false);
      await refresh();
    } catch (e) {
      toast.error(`${t("toasts.purge_all_failed")} — ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setPurgingAll(false);
    }
  };

  const trashCount = useMemo(() => docs.length, [docs]);

  return (
    <div className="animate-fade-up" data-testid="trash-page">
      {/* Back link */}
      <button
        onClick={() => navigate("/documents")}
        data-testid="trash-back-link"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-6 transition-colors"
      >
        <ArrowLeft size={14} /> {t("back_to_documents")}
      </button>

      {/* Header + purge-all action */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
            {t("kicker")}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950">
            {t("title")}
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl leading-relaxed">
            {t("subtitle")}
          </p>
        </div>

        <Button
          onClick={() => setConfirmPurgeAll(true)}
          disabled={!isOwner || purgingAll || trashCount === 0}
          data-testid="purge-all-button"
          title={!isOwner ? t("owner_only_hint") : undefined}
          variant="outline"
          className="h-9 rounded-lg text-xs border-qenva-cat-contract/40 text-qenva-cat-contract hover:bg-qenva-cat-contract/10 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {purgingAll
            ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> {t("table.purge_all_busy")}</>
            : <><Trash2 size={13} className="mr-1.5" /> {t("table.purge_all")}</>}
        </Button>
      </div>

      {/* List */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-zinc-400">{t("loading")}</div>
        ) : trashCount === 0 ? (
          <div className="p-14 text-center" data-testid="trash-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-zinc-100 grid place-items-center mb-4">
              <Trash2 size={22} className="text-zinc-500" strokeWidth={1.6} />
            </div>
            <p className="font-display text-base font-semibold text-zinc-900 mb-1">
              {t("empty.title")}
            </p>
            <p className="text-sm text-zinc-500 max-w-sm mx-auto">
              {t("empty.subtitle")}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100" data-testid="trash-list">
            {docs.map((d) => (
              <li
                key={d.id}
                data-testid={`trash-row-${d.id}`}
                className="flex items-center justify-between px-5 py-4 hover:bg-zinc-50 transition-colors gap-3"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-zinc-100 grid place-items-center shrink-0">
                    <DocTypeIcon type={d.document_type} size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-zinc-900 truncate">
                      {d.filename || d.title || d.id}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {t("table.deleted_on", {
                        date: d.deleted_at
                          ? formatDate(d.deleted_at, {
                              year: "numeric", month: "short", day: "numeric",
                              hour: "2-digit", minute: "2-digit",
                            })
                          : "—",
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => restore(d.id)}
                    disabled={restoringId === d.id}
                    data-testid={`restore-button-${d.id}`}
                    className="h-9 rounded-lg text-xs"
                  >
                    {restoringId === d.id
                      ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> {t("table.restoring")}</>
                      : <><RotateCcw size={13} className="mr-1.5" /> {t("table.restore")}</>}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmPurge(d)}
                    disabled={!isOwner || purgingId === d.id}
                    data-testid={`purge-button-${d.id}`}
                    title={!isOwner ? t("owner_only_hint") : undefined}
                    className="h-9 rounded-lg text-xs border-qenva-cat-contract/40 text-qenva-cat-contract hover:bg-qenva-cat-contract/10 disabled:opacity-50"
                  >
                    {purgingId === d.id
                      ? <><Loader2 size={12} className="mr-1.5 animate-spin" /> {t("table.purging")}</>
                      : <><Trash2 size={13} className="mr-1.5" /> {t("table.purge")}</>}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Per-doc purge confirmation */}
      <AlertDialog
        open={!!confirmPurge}
        onOpenChange={(open) => { if (!open) setConfirmPurge(null); }}
      >
        <AlertDialogContent data-testid="confirm-purge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="inline-flex items-center gap-2">
              <TriangleAlert size={16} className="text-qenva-cat-contract" />
              {t("confirm_purge.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm_purge.body", { name: confirmPurge?.filename || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-purge-cancel"
              disabled={!!purgingId}
            >
              {t("confirm_purge.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmPurge && purge(confirmPurge)}
              disabled={!!purgingId}
              data-testid="confirm-purge-confirm"
              className="bg-qenva-cat-contract hover:bg-qenva-cat-contract/90 text-white"
            >
              {purgingId
                ? <><Loader2 size={13} className="mr-1 animate-spin" /> {t("table.purging")}</>
                : t("confirm_purge.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Empty-all confirmation */}
      <AlertDialog
        open={confirmPurgeAll}
        onOpenChange={(open) => { if (!open) setConfirmPurgeAll(false); }}
      >
        <AlertDialogContent data-testid="confirm-purge-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="inline-flex items-center gap-2">
              <TriangleAlert size={16} className="text-qenva-cat-contract" />
              {t("confirm_purge_all.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm_purge_all.body", { count: trashCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="confirm-purge-all-cancel"
              disabled={purgingAll}
            >
              {t("confirm_purge_all.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={purgeAll}
              disabled={purgingAll}
              data-testid="confirm-purge-all-confirm"
              className="bg-qenva-cat-contract hover:bg-qenva-cat-contract/90 text-white"
            >
              {purgingAll
                ? <><Loader2 size={13} className="mr-1 animate-spin" /> {t("table.purge_all_busy")}</>
                : t("confirm_purge_all.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
